/**
 * Concurrent unfinished Drive trips (still "Current" until every pickup is taken).
 * Also holds a single "in process" draft (From/To planned, no order yet).
 */

import { archiveDriveTripToHistory } from "./driveTripHistory";

const LS_CURRENT = "smartRoute:currentTrips_v1";
const MAX_CURRENT = 8;
const TTL_MS = 12 * 60 * 60 * 1000;

function notify() {
  try {
    window.dispatchEvent(new Event("smartRoute:tripHistoryChanged"));
    window.dispatchEvent(new Event("smartRoute:multiStopPlanChanged"));
  } catch {
    /* ignore */
  }
}

function rid(id) {
  return String(id || "").trim();
}

function placeLabel(point) {
  return String(point?.formattedAddress || point?.address || "").trim();
}

function paidStops(plan) {
  return (Array.isArray(plan?.stops) ? plan.stops : []).filter(
    (s) => s?.status === "ordered" || s?.status === "ready" || s?.status === "taken",
  );
}

/** Paid stops still awaiting pickup — keep in Current trips regardless of TTL. */
function hasUnfinishedPaidStops(plan) {
  return (Array.isArray(plan?.stops) ? plan.stops : []).some(
    (s) => s?.status === "ordered" || s?.status === "ready",
  );
}

function isInProcess(plan) {
  return Boolean(plan?.inProcess || plan?.status === "in_process");
}

function withinTtl(iso) {
  const ms = iso ? new Date(iso).getTime() : NaN;
  if (!Number.isFinite(ms)) return true;
  return Date.now() - ms <= TTL_MS;
}

function normalizeInProcess(plan) {
  if (!plan || typeof plan !== "object" || !isInProcess(plan)) return null;
  const originLabel = placeLabel(plan.origin);
  const destLabel = placeLabel(plan.destination);
  if (!originLabel || !destLabel) return null;
  // Paid checkout dismisses this draft. A later server sync must not bring it back.
  if (isInProcessDismissed(originLabel, destLabel)) return null;
  const parkedAt = plan.parkedAt || new Date().toISOString();
  // Address-only drafts may expire; unfinished paid pickups never should.
  if (!withinTtl(parkedAt)) return null;
  return {
    ...plan,
    id: String(plan.id || `inprocess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
    inProcess: true,
    status: "in_process",
    origin: plan.origin,
    destination: plan.destination,
    stops: [],
    parkedAt,
    savedAt: Date.now(),
  };
}

function normalizeParked(plan) {
  if (!plan || typeof plan !== "object") return null;
  if (isInProcess(plan)) return normalizeInProcess(plan);
  const stops = paidStops(plan);
  if (!stops.length) return null;
  const parkedAt = plan.parkedAt || new Date().toISOString();
  // Keep until every pickup is taken — TTL must not drop ordered/ready trips.
  if (!withinTtl(parkedAt) && !hasUnfinishedPaidStops(plan)) return null;
  return {
    ...plan,
    id: String(plan.id || `current-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
    inProcess: false,
    stops,
    parkedAt,
    savedAt: Date.now(),
  };
}

export function loadCurrentDriveTrips() {
  try {
    const raw = sessionStorage.getItem(LS_CURRENT);
    const list = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(list)) return [];
    const next = list.map(normalizeParked).filter(Boolean);
    if (next.length !== list.length) persist(next);
    return next;
  } catch {
    return [];
  }
}

function persist(list, { notifyUi = true, sync = true } = {}) {
  try {
    sessionStorage.setItem(
      LS_CURRENT,
      JSON.stringify((Array.isArray(list) ? list : []).slice(0, MAX_CURRENT)),
    );
    if (notifyUi) notify();
    if (sync) {
      try {
        window.dispatchEvent(new Event("smartRoute:driveTripLocalChanged"));
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* ignore */
  }
}

export function saveCurrentDriveTrips(list) {
  persist(list);
  return loadCurrentDriveTrips();
}

/**
 * Replace current trips from server without immediately pushing back.
 */
export function hydrateCurrentDriveTrips(list, { notifyUi = true } = {}) {
  const next = (Array.isArray(list) ? list : []).map(normalizeParked).filter(Boolean);
  persist(next, { notifyUi, sync: false });
  return next;
}

/** Save / replace the single in-process draft (addresses planned, no order yet). */
export function saveInProcessDriveTrip({
  origin,
  destination,
  routeIndex,
  routeDurationMinutes,
  departureIso,
  /** true when user explicitly planned via Add trip (overrides a prior Remove draft) */
  allowAfterDismiss = false,
} = {}) {
  const o = placeLabel(origin);
  const d = placeLabel(destination);
  if (allowAfterDismiss) {
    clearInProcessDismissed(o, d);
  } else if (o && d && isInProcessDismissed(o, d)) {
    return null;
  }

  const draft = normalizeInProcess({
    id: `inprocess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    inProcess: true,
    status: "in_process",
    origin: origin || null,
    destination: destination || null,
    routeIndex: routeIndex != null ? Number(routeIndex) : 0,
    routeDurationMinutes:
      Number.isFinite(Number(routeDurationMinutes)) && Number(routeDurationMinutes) > 0
        ? Number(routeDurationMinutes)
        : null,
    departureIso: departureIso || null,
    stops: [],
    parkedAt: new Date().toISOString(),
  });
  if (!draft) return null;

  const kept = loadCurrentDriveTrips().filter((t) => !isInProcess(t));
  persist([draft, ...kept]);
  return draft;
}

const LS_DISMISSED = "smartRoute:inProcessDismissed_v1";

function dismissalKey(originLabel, destLabel) {
  return `${String(originLabel || "").trim().toLowerCase()}→${String(destLabel || "").trim().toLowerCase()}`;
}

function readDismissedKeys() {
  try {
    const raw = sessionStorage.getItem(LS_DISMISSED);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.map(String) : [];
  } catch {
    return [];
  }
}

function writeDismissedKeys(keys) {
  try {
    sessionStorage.setItem(LS_DISMISSED, JSON.stringify((keys || []).slice(0, 20)));
  } catch {
    /* ignore */
  }
}

function isInProcessDismissed(originLabel, destLabel) {
  const key = dismissalKey(originLabel, destLabel);
  if (!key || key === "→") return false;
  return readDismissedKeys().includes(key);
}

function clearInProcessDismissed(originLabel, destLabel) {
  const key = dismissalKey(originLabel, destLabel);
  if (!key || key === "→") return;
  writeDismissedKeys(readDismissedKeys().filter((k) => k !== key));
}

function markInProcessDismissed(originLabel, destLabel) {
  const key = dismissalKey(originLabel, destLabel);
  if (!key || key === "→") return;
  const keys = readDismissedKeys().filter((k) => k !== key);
  keys.unshift(key);
  writeDismissedKeys(keys);
}

/** Drop all address-only in-process drafts (e.g. wipe when endpoints unknown). */
export function clearInProcessDriveTrips() {
  const kept = loadCurrentDriveTrips().filter((t) => !isInProcess(t));
  persist(kept);
  return kept;
}

/** Cancel every Add-trip draft so a different order cannot reuse it. */
export function dismissAllInProcessDriveTrips() {
  const list = loadCurrentDriveTrips();
  for (const trip of list) {
    if (!isInProcess(trip)) continue;
    markInProcessDismissed(placeLabel(trip.origin), placeLabel(trip.destination));
  }
  const kept = list.filter((trip) => !isInProcess(trip));
  if (kept.length !== list.length) persist(kept);
  return kept;
}

/** Remove one in-process draft and remember so auto-replan won't recreate it. */
export function dismissInProcessDriveTrip(tripId) {
  const id = String(tripId || "");
  const list = loadCurrentDriveTrips();
  const target = list.find((t) => String(t.id) === id && isInProcess(t));
  if (target) {
    markInProcessDismissed(placeLabel(target.origin), placeLabel(target.destination));
  }
  const next = list.filter((t) => String(t.id) !== id);
  persist(next);
  return next;
}

function labelsLooselySame(a, b) {
  const left = placeLabel(a).toLowerCase();
  const right = placeLabel(b).toLowerCase();
  if (!left || !right) return false;
  if (left === right) return true;
  const shorter = left.length <= right.length ? left : right;
  const longer = shorter === left ? right : left;
  return longer.includes(shorter);
}

/**
 * Drop Add-trip drafts that are not this order's departure.
 * A matching draft stays so the paid Global Drive route can replace it.
 */
export function dismissInProcessDriveTripsExceptOrigin(origin) {
  const want = placeLabel(origin);
  const list = loadCurrentDriveTrips();
  const kept = list.filter((trip) => {
    if (!isInProcess(trip)) return true;
    const same =
      Boolean(want) &&
      (placeLabel(trip.origin) === want || labelsLooselySame(trip.origin, origin));
    if (same) return true;
    markInProcessDismissed(placeLabel(trip.origin), placeLabel(trip.destination));
    return false;
  });
  if (kept.length !== list.length) persist(kept);
  return kept;
}

/**
 * After a paid order, remove the Global Drive draft for this route.
 * The draft's To is often the street, while checkout still has the restaurant name.
 */
export function clearInProcessDriveTripForEndpoints({ origin, destination, restaurantId } = {}) {
  const o = placeLabel(origin);
  const d = placeLabel(destination);
  const wantId = String(restaurantId || destination?.restaurantId || "").trim();
  if (!o && !d && !wantId) return clearInProcessDriveTrips();
  const kept = loadCurrentDriveTrips().filter((t) => {
    if (!isInProcess(t)) return true;
    const destId = String(t.destination?.restaurantId || "").trim();
    const sameRestaurant = Boolean(wantId && destId && wantId === destId);
    const sameOrigin = !o || placeLabel(t.origin) === o || labelsLooselySame(t.origin, origin);
    const sameDest = !d || placeLabel(t.destination) === d || labelsLooselySame(t.destination, destination);
    if (!(sameRestaurant || (sameOrigin && sameDest))) return true;
    markInProcessDismissed(placeLabel(t.origin), placeLabel(t.destination));
    return false;
  });
  persist(kept);
  return kept;
}

/** Stable fingerprint of paid orders — restaurant alone is not unique (concurrent same-shop trips). */
function paidOrderFingerprint(t) {
  const ids = [];
  for (const s of paidStops(t)) {
    for (const oid of [
      ...(Array.isArray(s.previousOrderIds) ? s.previousOrderIds : []),
      s.orderId,
    ]) {
      const id = String(oid || "").trim();
      if (id) ids.push(id);
    }
  }
  return ids.sort().join(",");
}

/** Park an unfinished trip with paid stops into Current trips. */
export function parkDriveTrip(plan) {
  const parked = normalizeParked({
    ...plan,
    inProcess: false,
    id: plan?.id || `current-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    parkedAt: new Date().toISOString(),
  });
  if (!parked || isInProcess(parked)) return null;

  const parkedOrders = paidOrderFingerprint(parked);
  const existing = loadCurrentDriveTrips().filter((t) => String(t.id) !== String(parked.id));
  // Only drop an exact duplicate (same From/To AND same paid order ids).
  // Same restaurant / corridor with different tokens must stay in Current trips.
  const filtered = existing.filter((t) => {
    if (isInProcess(t)) return true;
    const sameOrigin = placeLabel(t.origin) === placeLabel(parked.origin);
    const sameDest = placeLabel(t.destination) === placeLabel(parked.destination);
    if (!sameOrigin || !sameDest) return true;
    const otherOrders = paidOrderFingerprint(t);
    // Empty fingerprints (no order ids yet) still compare restaurants to avoid draft dupes.
    if (!parkedOrders && !otherOrders) {
      const restKey = (x) =>
        paidStops(x)
          .map((s) => rid(s.restaurantId))
          .filter(Boolean)
          .sort()
          .join(",");
      return restKey(t) !== restKey(parked);
    }
    return otherOrders !== parkedOrders;
  });
  persist([parked, ...filtered]);
  return parked;
}

export function updateCurrentDriveTrip(tripId, plan) {
  const id = String(tripId || "");
  if (!id) return loadCurrentDriveTrips();
  const parked = normalizeParked({
    ...plan,
    id,
    parkedAt: plan?.parkedAt || new Date().toISOString(),
  });
  const list = loadCurrentDriveTrips();
  if (!parked) {
    return saveCurrentDriveTrips(list.filter((t) => String(t.id) !== id));
  }
  const without = list.filter((t) => String(t.id) !== id);
  return saveCurrentDriveTrips([parked, ...without]);
}

export function removeCurrentDriveTrip(tripId) {
  const id = String(tripId || "");
  if (!id) return loadCurrentDriveTrips();
  return saveCurrentDriveTrips(loadCurrentDriveTrips().filter((t) => String(t.id) !== id));
}

/**
 * Mark prêt à récupérer on a parked Current trip stop (mirrors active-plan markDriveOrderReady).
 * @param {string} orderId
 * @param {{ readyAt?: string|null, readyNotificationSentAt?: string|null }} [opts]
 * @returns {{ plan: object|null, stop: object|null, changed: boolean }}
 */
export function markCurrentDriveTripOrderReady(orderId, opts = {}) {
  const oid = String(orderId || "").trim();
  if (!oid) return { plan: null, stop: null, changed: false };

  const readyAt =
    opts.readyAt ||
    opts.readyNotificationSentAt ||
    new Date().toISOString();

  const list = loadCurrentDriveTrips();
  let matched = null;
  let matchedStop = null;
  let changed = false;
  const nextList = [];

  for (const trip of list) {
    if (isInProcess(trip)) {
      nextList.push(trip);
      continue;
    }
    let hit = false;
    let tripChanged = false;
    // Per-iteration binding so the .map callback does not close over outer matchedStop (no-loop-func).
    let tripMatchedStop = matchedStop;
    const stops = (trip.stops || []).map((s) => {
      const stopIds = [
        ...(Array.isArray(s.previousOrderIds) ? s.previousOrderIds : []),
        s.orderId,
      ]
        .map((id) => String(id || "").trim())
        .filter(Boolean);
      if (!stopIds.includes(oid)) return s;
      hit = true;
      if (s.status === "taken") {
        tripMatchedStop = tripMatchedStop || s;
        return s;
      }
      if (s.status === "ready") {
        tripMatchedStop = s;
        if (!s.readyAt && readyAt) {
          tripChanged = true;
          tripMatchedStop = { ...s, readyAt, orderStatus: "ready" };
          return tripMatchedStop;
        }
        return s;
      }
      if (s.status !== "ordered" && s.status !== "open") return s;
      tripChanged = true;
      tripMatchedStop = {
        ...s,
        status: "ready",
        orderStatus: "ready",
        readyAt,
      };
      return tripMatchedStop;
    });
    matchedStop = tripMatchedStop;

    if (!hit) {
      nextList.push(trip);
      continue;
    }

    if (tripChanged) {
      changed = true;
      const updated = { ...trip, stops, savedAt: Date.now() };
      matched = updated;
      nextList.push(updated);
    } else {
      matched = trip;
      nextList.push(trip);
    }
  }

  if (changed) persist(nextList);
  return { plan: matched, stop: matchedStop, changed };
}

export function markCurrentDriveTripOrderTaken(orderId, opts = {}) {
  const oid = String(orderId || "").trim();
  if (!oid) return { plan: null, tripClosed: false, archivedTrip: null };

  const completedSet = new Set(
    (Array.isArray(opts.completedOrderIds) ? opts.completedOrderIds : [])
      .map((id) => String(id || "").trim())
      .filter(Boolean),
  );
  completedSet.add(oid);

  const list = loadCurrentDriveTrips();
  let matched = null;
  let tripClosed = false;
  let archivedTrip = null;
  const nextList = [];

  for (const trip of list) {
    if (isInProcess(trip)) {
      nextList.push(trip);
      continue;
    }
    let hit = false;
    const takenAt = new Date().toISOString();
    const stops = (trip.stops || []).map((s) => {
      const stopIds = [
        ...(Array.isArray(s.previousOrderIds) ? s.previousOrderIds : []),
        s.orderId,
      ]
        .map((id) => String(id || "").trim())
        .filter(Boolean);
      if (!stopIds.includes(oid)) return s;
      hit = true;
      if (s.status === "taken") return s;
      const takenSoFar = new Set([
        ...(Array.isArray(s.takenOrderIds) ? s.takenOrderIds.map(String) : []),
        ...completedSet,
      ]);
      const allTaken = stopIds.every((id) => takenSoFar.has(id));
      if (!allTaken) {
        return { ...s, takenOrderIds: [...takenSoFar].filter((id) => stopIds.includes(id)) };
      }
      return {
        ...s,
        status: "taken",
        takenAt,
        takenOrderIds: stopIds,
      };
    });

    if (!hit) {
      nextList.push(trip);
      continue;
    }

    const updated = { ...trip, stops, savedAt: Date.now() };
    matched = updated;
    const allTaken = paidStops(updated).every((s) => s.status === "taken");
    if (allTaken) {
      archivedTrip = archiveDriveTripToHistory(updated, { closedAt: takenAt });
      tripClosed = true;
    } else {
      nextList.push(updated);
    }
  }

  if (matched) persist(nextList);
  return { plan: matched, tripClosed, archivedTrip };
}

export function findCurrentDriveTripByOrderId(orderId) {
  const oid = String(orderId || "").trim();
  if (!oid) return null;
  for (const trip of loadCurrentDriveTrips()) {
    if (isInProcess(trip)) continue;
    for (const s of trip.stops || []) {
      const ids = [
        ...(Array.isArray(s.previousOrderIds) ? s.previousOrderIds : []),
        s.orderId,
      ]
        .map((id) => String(id || "").trim())
        .filter(Boolean);
      if (ids.includes(oid)) return trip;
    }
  }
  return null;
}

export function getCurrentDriveOrderedRestaurantIds() {
  const ids = new Set();
  for (const trip of loadCurrentDriveTrips()) {
    if (isInProcess(trip)) continue;
    for (const s of paidStops(trip)) {
      if (s?.restaurantId) ids.add(rid(s.restaurantId));
    }
  }
  return ids;
}

/** The single address-only in-process draft, if any. */
export function getInProcessDriveTrip() {
  return loadCurrentDriveTrips().find(isInProcess) || null;
}

export { isInProcess as isInProcessDriveTrip };
